<template>
  <component-wrapper class="product-category">
    <grid-container-fi v-if="textsMap.BEZEICHNUNG && !isAppView">
      <headline-fi
        v-if="textsMap.BEZEICHNUNG"
        class="product-category__name fi-col-span-12 fi-col-start-1 sm:fi-col-span-10 sm:fi-col-start-2 lg:fi-col-span-6 lg:fi-col-start-4"
        tag="h1"
        :size="60"
        :html-content="textsMap.BEZEICHNUNG"
      />
    </grid-container-fi>
    <loading-indicator v-if="categorySearchMode === 'pending'" />
    <div
      v-else-if="categorySearchMode === 'subcategoriesOnly'"
      class="fi-col-span-full fi-mx-auto fi-grid fi-max-w-xl fi-grid-cols-1 fi-gap-large fi-px-9 xxs:fi-grid-cols-2 sm:fi-grid-cols-3 md:fi-px-18 lg:fi-grid-cols-5"
    >
      <fact-finder-item-fi
        v-for="(category, idx) in childCategories"
        :key="category.CategoryId"
        :record="{
          score: 0,
          masterValuesObject: {
            catalogCategoryData: category,
            catalogSeriesData: null,
            masterProductData: null,
          },
        }"
        :record-idx="idx"
        :show-description="false"
        type="family"
      />
    </div>
    <search-fact-finder-results-fi
      v-else
      query-prefix="[category]:"
      click-source="category"
      :category-path-id-root="categoryPathIdRoot"
      attach-breadcrumb
    >
      <template #withResults="{ searchFactFinderNg }">
        <ld-json
          v-if="searchFactFinderNg?.masters"
          :json="toLdJson(searchFactFinderNg)"
        />
      </template>
    </search-fact-finder-results-fi>
    <grid-container-fi
      v-if="showCategoryDescription && (textsMap.DOKBSTMETADESCRTITLE || categoryDescription)"
    >
      <div
        class="fi-col-start-1 fi-col-end-13 sm:fi-col-start-2 sm:fi-col-end-12 md:fi-col-start-3 md:fi-col-end-11 lg:fi-col-start-4 lg:fi-col-end-10"
      >
        <div class="product-category__description">
          <headline-fi
            v-if="textsMap.DOKBSTMETADESCRTITLE"
            tag="h2"
            :size="48"
            class="product-category__description__headline"
          >
            {{ textsMap.DOKBSTMETADESCRTITLE }}
          </headline-fi>

          <richtext-fi
            class="product-category__description__text"
            :html-content="{ value: categoryDescription }"
          />
        </div>
      </div>
    </grid-container-fi>
    <section-divider-small-fi />
  </component-wrapper>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { HeadlineFi, LdJson, LoadingIndicator, RichtextFi } from 'atoms';
import { computed, onMounted, onServerPrefetch, provide, ref, watch } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import FactFinderItemFi from '@/components/search-fact-finder-results-fi/fact-finder-item-fi/FactFinderItemFi.vue';
import SearchFactFinderResultsFi from '@/components/search-fact-finder-results-fi/SearchFactFinderResultsFi.vue';
import { useAppEvent } from '@/composables/appEvent';
import { useFischer } from '@/composables/fischerPlugin';
import { useSalesChannelFilter } from '@/composables/salesChannelFilter';
import type {
  GetProductCateogoryDataQuery,
  GetProductCateogoryDataQueryVariables,
  SearchFactFinderNgQuery,
} from '@/generated/graphql';
import GET_PRODUCT_CATEGORY_DATA from '@/graphql/getProductCategoryData.gql';
import SectionDividerSmallFi from '@/pages/section-divider-fi/SectionDividerSmallFi.vue';
import { useNotificationStore } from '@/store/pinia/notification';

defineOptions({
  name: 'ProductCategory',
});

const fischer = useFischer();
const notificationStore = useNotificationStore();
const { getSalesChannels, isPublicAssortment } = useSalesChannelFilter();
const { sendAppEvent } = useAppEvent();

const categoryPathIdRoot = computed(() => fischer.catalogId('1001340176'));

const isMounted = ref(false);
onMounted(() => (isMounted.value = true));

// R1: product category data query — SEO-critical content (heading, description, subcategories), needs R0
const {
  data: getCategoryById,
  error: categoryError,
  executeQuery,
} = useQuery<GetProductCateogoryDataQuery, GetProductCateogoryDataQueryVariables>({
  query: GET_PRODUCT_CATEGORY_DATA,
  variables: computed(() => ({
    id: categoryPathIdRoot.value ?? '',
    baseSiteId: fischer.baseSiteId(),
    language: fischer.catalogLocale(),
    getSubCategories: categoryPathIdRoot.value?.startsWith('sf-') ?? false,
    publicAssortment: isPublicAssortment(),
    salesChannels: getSalesChannels(),
  })),
});

// immediate + flush:'sync' - see createSsrExchange. immediate: a restored SSR result is
// delivered during setup, so a lazy watcher never sees it. sync: Vue drops non-sync watchers
// during SSR entirely, so without it this state is never filled on the server.
// immediate: restored SSR results are delivered synchronously during setup, so a lazy watcher
// would never see them and the title event would never be sent after hydration.
watch(
  getCategoryById,
  (data) => {
    if (data?.getCategoryById) {
      sendAppEvent('set-title', { title: data.getCategoryById.CategoryName });
    }
  },
  { immediate: true, flush: 'sync' },
);

watch(categoryError, (error) => {
  if (error) notificationStore.openGeneralErrorNotification();
});

// R0: category heading/description/subcategories are SEO-critical, must be on first SSR paint
onServerPrefetch(async () => {
  await executeQuery();
});

const childCategories = computed(
  () =>
    getCategoryById.value?.getCategoryById?.childCategories?.filter(
      (category): category is NonNullable<typeof category> => category !== null,
    ) ?? [],
);

type CategorySearchMode = 'fullSearch' | 'subcategoriesOnly' | 'pending';

const categorySearchMode = computed<CategorySearchMode>(() => {
  if (!categoryPathIdRoot.value?.startsWith('sf-')) {
    return 'fullSearch';
  }
  if (!isMounted.value) {
    return 'pending';
  }
  if (categoryPathIdRoot.value === 'sf-root') {
    return 'subcategoriesOnly';
  }
  const length = getCategoryById.value?.getCategoryById?.CategoryPath?.length;
  if (!length) {
    return 'pending';
  }
  if (length > 1 || childCategories.value.length === 0) {
    return 'fullSearch';
  }
  return 'subcategoriesOnly';
});

const textsMap = computed<Record<string, string>>(() => {
  const map: Record<string, string> = {};
  getCategoryById.value?.getCategoryById?.TextList?.forEach((item) => {
    if (item && item.Key && item?.Value) {
      map[item.Key] = item.Value;
    }
  });
  return map;
});

const categoryDescription = computed<string>(() => {
  return textsMap.value.SEO_DETAILS || textsMap.value.DOKBSTMARKDESCR || '';
});

const showCategoryDescription = computed<boolean>(() => fischer.isSitecoreContent());

type FactFinderMasters = NonNullable<
  NonNullable<SearchFactFinderNgQuery['searchFactFinderNg']>['masters']
>;
type FactFinderMasterHit = NonNullable<FactFinderMasters['hits'][number]>;

const toLdJson = ({ masters }: { masters: FactFinderMasters }) => {
  const base = `${fischer.hostUrlBase()}${fischer.pages.products().url}`;
  const jsonLdList = masters.hits
    .filter((item): item is FactFinderMasterHit => item !== null)
    .filter((item) => item.masterValuesObject?.catalogCategoryData?.Slug)
    .map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: `${base}${item.masterValuesObject?.catalogCategoryData?.Slug}`,
      name: item.masterValuesObject?.catalogCategoryData?.CategoryName,
      image: item.masterValuesObject?.catalogCategoryData?.imageUrl,
    }));

  return JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': 'ItemList',
    numberOfItems: masters.totalHits,
    itemListElement: jsonLdList,
  });
};

provide('getSearchData', () => ({
  query: '',
  pageSize: 200,
  attachBreadcrumb: true,
  clickSource: 'category',
}));
</script>

<style scoped src="./product-category" />
